import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import moment from 'moment/moment';
import {Alert, Button, DatePicker, Spin} from 'antd';
import axios from 'axios';
import {displayAmount, roundOutPenny} from '../utils/money';
import MoneyDisplay from '../common/MoneyDisplay';
import {authorizationHeaders} from '../utils/api';
import {roundAmount} from '../utils/money';
import './TransactionHistoryReportPage.css';

export function sum(items, prop) {
  return items.reduce((total, item) => total + item[prop], 0);
}

function formatRateForDisplay(rate, precision = 2) {
  return `${roundAmount(rate * 100, precision)}%`;
}

const styles = {
  floatRight: {
    float: 'right',
  },
};

const maxResults = 10000;

function formatDiscountRate(item) {
  if (!item.discountRate) return "";
  return ` - ${formatRateForDisplay(item.discountRate)} discount, reg. price ${displayAmount(item.amount)}`;
}

function formatPaidAmount(item) {
  if (item.discountRate)
    return displayAmount(item.amountWithDiscount);
  else
    return displayAmount(item.amount);
}

export class TransactionHistoryReportPage extends Component {
  static propTypes = {
    dispensaryId: PropTypes.string.isRequired,
  };

  state = {
    dateTimeFrom: moment().startOf('day'),
    dateTimeTo: moment().endOf('day'),
    error: '',
    loading: false,
    receipts: [],
  };

  componentDidMount() {
    this._loadReceipts();
  }

  _loadReceipts() {
    const filter = {
      where: {
        dispensaryId: {eq: this.props.dispensaryId},
        datetime: {between: [this.state.dateTimeFrom, this.state.dateTimeTo]},
      },
      limit: maxResults,
      order: 'datetime DESC',
    };
    this.setState({loading: true, error: ''});
    axios.get(
      `${process.env.PUBLIC_URL}/api/v1/r/transactionHistoryReport?filter=${encodeURIComponent(JSON.stringify(filter))}&_=${Date.now()}`,
      {headers: authorizationHeaders()}
    ).then(res => this.setState({receipts: res.data}), err => this.setState({error: err.message}))
      .finally(() => this.setState({loading: false}));
  }

  _onRangePickerChange = (values) => {
    const [dateTimeFrom, dateTimeTo] = values;
    if (!dateTimeFrom || !dateTimeTo) return;
    this.setState({dateTimeFrom, dateTimeTo});
  };

  _print = () => window.print();

  render() {
    const {dateTimeFrom, dateTimeTo, loading, receipts} = this.state;
    const {salesTotal, savingsTotal, taxesTotal, cashTotal} = receipts.reduce(
      ({salesTotal, savingsTotal, taxesTotal, cashTotal}, receipt) => {
        salesTotal += receipt.subTotal;
        savingsTotal += receipt.totalSavings || 0;
        cashTotal += roundOutPenny(receipt.total);
        for (const tax of receipt.taxes) {
          const taxTotal = taxesTotal.find(t => t.taxId === tax.taxId) || (taxesTotal[taxesTotal.length] = {...tax, amount: 0});
          taxTotal.amount += tax.amount;
        }
        return {salesTotal, savingsTotal, taxesTotal, cashTotal};
      },
      {
        salesTotal: 0,
        savingsTotal: 0,
        taxesTotal: [],
        cashTotal: 0,
      }
    );
    return (
      <div>
        <div className="transaction-history-report-form">
          <DatePicker.RangePicker showTime={{
            defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('11:59:59', 'HH:mm:ss')],
          }}
                       format="YYYY-MM-DD HH:mm:ss"
                       value={[dateTimeFrom, dateTimeTo]}
                       onChange={this._onRangePickerChange}
                       onOk={() => this._loadReceipts()}/>
          <Button icon="printer" style={styles.floatRight} onClick={this._print}>Print</Button>
          {this._renderAlert()}
        </div>
        <Spin spinning={loading}>
          <h1>Transaction History</h1>
          {receipts.map((receipt, index) =>
            <div key={index} className="transaction-history-receipt">
              <div className="receipt-info">
                <div>Date: {moment(receipt.datetime, moment.ISO_8601).format('YYYY-MM-DD HH:ss')}</div>
                <div>Order#: {receipt.orderId.substr(0, 8)}</div>
              </div>
              <div className="receipt-info">
                <div>Cashier: {receipt.cashierName}</div>
                <div>Customer: {receipt.customerName}{receipt.customerStatus && ` (${receipt.customerStatus})`}</div>
              </div>
              <div className="receipt-items">
                {receipt.items.map((item) => <div key={item.orderItemId}>
                  {item.quantity}x {item.productName} <span style={styles.floatRight}>{formatPaidAmount(item)}</span>
                  {(!!item.discountRate) && <div>{formatDiscountRate(item)}</div>}
                  {item.fees.map(fee => <div key={fee.feeId}>&nbsp;&nbsp;-&nbsp;{fee.display}
                    <span style={styles.floatRight}><MoneyDisplay amount={fee.amount}/></span>
                  </div>)}
                </div>)}
              </div>
              <div className="receipt-totals">
                <div>Subtotal <span style={styles.floatRight}><MoneyDisplay amount={receipt.subTotal}/></span></div>
                <br/>
                {receipt.taxes
                  .filter(x => x.show)
                  .map((tax) => <div key={tax.taxId}>
                    {tax.name} {formatRateForDisplay(tax.rate)} {tax.registrationNumber && `(${tax.registrationNumber})`}
                    <span style={styles.floatRight}>{tax.exemption
                      ? "Exempted"
                      : <MoneyDisplay amount={tax.amount}/>
                    }</span>
                  </div>)}
                <br/>
                <div>Total <span style={styles.floatRight}><MoneyDisplay amount={receipt.total}/></span></div>
                <div>Total (Cash)<span style={styles.floatRight}><MoneyDisplay
                  amount={roundOutPenny(receipt.total)}/></span></div>
              </div>
              <div className="receipt-payment">
                <div>Cash Payment</div>
                <div>Cash <span style={styles.floatRight}><MoneyDisplay amount={receipt.cash}/></span></div>
                <div>Change <span style={styles.floatRight}><MoneyDisplay amount={receipt.change}/></span></div>
              </div>
              {(!!receipt.totalSavings) &&
              <div className="receipt-savings">
                <div>Your total Savings <span style={styles.floatRight}><MoneyDisplay
                  amount={receipt.totalSavings}/></span></div>
                <div>Percentage Savings <span
                  style={styles.floatRight}>{formatRateForDisplay((receipt.totalSavings / sum(receipt.items, 'amount')), 0)}</span></div>
              </div>}
            </div>
          )}
          <div className="transaction-history-summary">
            <div className="transaction-history-subtotal">
              <div style={{flex: 1}}>Subtotal</div>
              <div style={{flex: 2}}>
                <div>
                  <div>Sales</div>
                  <div><MoneyDisplay amount={salesTotal} /></div>
                </div>
                <div>
                  <div>Savings</div>
                  <div><MoneyDisplay amount={savingsTotal} /></div>
                </div>
                {taxesTotal.map((tax, index) =>
                  <div key={index}>
                    <div>{tax.name} {formatRateForDisplay(tax.rate)}</div>
                    <div><MoneyDisplay amount={tax.amount} /></div>
                  </div>
                )}
              </div>
            </div>
            <div className="transaction-history-total">
              <div style={{flex: 1}}>Total</div>
              <div style={{flex: 2}}>
                <div>Cash</div>
                <MoneyDisplay amount={cashTotal} />
              </div>
            </div>
          </div>
        </Spin>
      </div>
    );
  }

  _renderAlert() {
    const {error, receipts} = this.state;
    let type, message, description;
    if (error) {
      type = 'error';
      message = 'Error';
      description = error;
    } else if (receipts.length === 0) {
      type = 'info';
      message = "No transaction found";
      description = "Choose a different date range";
    } else if (receipts.length === maxResults) {
      type = 'warning';
      message = "Too many transactions";
      description = "Choose a smaller date range";
    } else {
      return null;
    }
    return (<Alert type={type} message={message} description={description} />);
  }
}

const mapStateToProps = (state) => ({
  dispensaryId: state.dispensary.selectedDispensaryId,
});

export default connect(mapStateToProps)(TransactionHistoryReportPage);



// WEBPACK FOOTER //
// ./src/Reports/TransactionHistoryReportPage.js